// Plasiyer 360 — "onaycının kararı için gereken veri ekranda" (spec §6.3).
//
// GET /api/vehicles/loads/{id}/plasiyer-360 döner:
//   creditLimit {status, availableLimit} | null (servis KAPALIYSA null → satır render EDİLMEZ)
//   openBalance / overdueAmount → şu an NULL: tüccar plasiyerin carisi SAP'de tutulur (§2.1.1.3),
//     bizim ledger'ımızda karşılığı yok. Uydurmuyoruz, "SAP" notuyla boş gösteriyoruz.
//   recentLoads[] (son 4 yükleme) · vehicleStock[] (aracın MEVCUT stoğu)
//
// Sapma (%X) ve stok devir hızı burada İSTEMCİ TARAFINDA hesaplanır (backend dönmüyor) ve
// onMetrics ile yukarı verilir — uyarı bandı sapmayı oradan gösterir.
import { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../listPageStyles';
import { StatusBadge } from '../ui';
import {
  API_BASE, pick, unwrap, formatMoney, formatNumber, formatDate,
  statusBadge, CREDIT_LIMIT_META,
} from './vehicleLoadShared';
import './vehicleLoad.css';

/**
 * @param {string} loadId
 * @param {number} netAmount        bu talebin tutarı (sapma hesabı için)
 * @param {Array}  requestedLines   [{productId, productName, requestedQty}] — "talep ettiği ürün elinde mi?"
 * @param {(m:{deviationPct:number|null}) => void} onMetrics
 */
export function Plasiyer360Panel({ loadId, netAmount = 0, requestedLines = [], onMetrics }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllStock, setShowAllStock] = useState(false);

  const load = useCallback(async () => {
    if (!loadId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${loadId}/plasiyer-360`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Plasiyer 360 alınamadı (${res.status})`);
      setData(unwrap(await res.json()) || {});
    } catch (err) {
      setError(err.message || 'Plasiyer 360 alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [loadId]);

  useEffect(() => { load(); }, [load]);

  const recentLoads = data?.recentLoads || [];
  const vehicleStock = data?.vehicleStock || [];
  const creditLimit = data?.creditLimit || null;

  // Son yüklemelerin ortalaması → bu talebin sapması.
  const avgNet = recentLoads.length
    ? recentLoads.reduce((sum, r) => sum + Number(pick(r, 'netAmount', 'NetAmount') || 0), 0) / recentLoads.length
    : 0;
  const avgCases = recentLoads.length
    ? recentLoads.reduce((sum, r) => sum + Number(pick(r, 'totalCases', 'TotalCases') || 0), 0) / recentLoads.length
    : 0;
  const deviationPct = avgNet > 0 ? Math.round(((Number(netAmount) - avgNet) / avgNet) * 100) : null;

  useEffect(() => {
    if (!loading && onMetrics) onMetrics({ deviationPct });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, deviationPct]);

  const stockTotal = vehicleStock.reduce((sum, s) => sum + Number(pick(s, 'physical', 'Physical') || 0), 0);
  // Devir hızı ≈ elde kalan stok / günlük ortalama çıkış. Yükleme frekansı ~haftalık kabul edilir
  // (son 4 yükleme ≈ 4 hafta) → günlük çıkış = ortalama koli / 7. Kaba TAHMİN, "≈" ile gösterilir.
  const dailyOut = avgCases > 0 ? avgCases / 7 : 0;
  const turnoverDays = dailyOut > 0 ? stockTotal / dailyOut : null;

  // Talep edilen ürünlerin araçtaki mevcut stoğu — "istediği mal zaten elinde duruyor mu?"
  const requestedIds = new Set(requestedLines.map((l) => l.productId).filter(Boolean));
  const stockRows = vehicleStock
    .map((sRow) => ({
      productId: pick(sRow, 'productId', 'ProductId'),
      productCode: pick(sRow, 'productCode', 'ProductCode'),
      productName: pick(sRow, 'productName', 'ProductName') || '(ürün kartı yok)',
      physical: Number(pick(sRow, 'physical', 'Physical') || 0),
    }))
    // Talep edilen ürünler ÖNCE: onaycı asıl bunlara bakacak.
    .sort((a, b) => (requestedIds.has(b.productId) ? 1 : 0) - (requestedIds.has(a.productId) ? 1 : 0));
  const visibleStock = showAllStock ? stockRows : stockRows.slice(0, 6);

  if (loading) {
    return <div className="vl-card"><p className="vl-card-title">Plasiyer 360</p><div style={{ fontSize: 12, color: '#64748b' }}>Yükleniyor…</div></div>;
  }
  if (error) {
    return <div className="vl-card"><p className="vl-card-title">Plasiyer 360</p><div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div></div>;
  }

  const repName = pick(data, 'representativeName', 'RepresentativeName') || '—';

  return (
    <div className="vl-card">
      <p className="vl-card-title">Plasiyer 360 · {repName}</p>

      {/* Kredi limiti — servis KAPALIYSA backend null döner, satır hiç render edilmez (spec §4.3). */}
      {creditLimit ? (
        <>
          <div className="vl-kv">
            <span>Limit durumu</span>
            <StatusBadge tone={(CREDIT_LIMIT_META[creditLimit.status] || {}).tone || 'neutral'}>
              {(CREDIT_LIMIT_META[creditLimit.status] || {}).label || creditLimit.status}
            </StatusBadge>
          </div>
          <div className="vl-kv">
            <span>Kullanılabilir limit</span>
            <strong style={{ color: creditLimit.status === 'exceeded' ? '#dc2626' : undefined }}>
              {creditLimit.availableLimit == null ? '—' : formatMoney(creditLimit.availableLimit)}
            </strong>
          </div>
        </>
      ) : (
        <div className="vl-kv"><span>Kredi limiti</span><span style={{ color: '#64748b' }}>Limit servisi kapalı</span></div>
      )}
      <div className="vl-kv">
        <span>Açık bakiye</span>
        <span style={{ color: '#64748b' }}>
          {data?.openBalance == null ? '— (SAP carisi)' : formatMoney(data.openBalance)}
        </span>
      </div>
      <div className="vl-kv">
        <span>Vadesi geçmiş</span>
        <span style={{ color: '#64748b' }}>
          {data?.overdueAmount == null ? '— (SAP carisi)' : formatMoney(data.overdueAmount)}
        </span>
      </div>

      <div className="vl-kv" style={{ marginTop: 6 }}>
        <span>Son {recentLoads.length || 0} yükleme ort.</span>
        <span>{recentLoads.length ? `${formatMoney(avgNet)} · ${formatNumber(avgCases)} koli` : '—'}</span>
      </div>
      <div className="vl-kv">
        <span>Bu talebin sapması</span>
        <span style={{ color: deviationPct != null && Math.abs(deviationPct) >= 30 ? '#d97706' : undefined, fontWeight: 700 }}>
          {deviationPct == null ? '—' : `${deviationPct > 0 ? '+' : ''}%${deviationPct}`}
        </span>
      </div>
      <div className="vl-kv">
        <span>Araçtaki toplam stok</span>
        <span>{formatNumber(stockTotal)} birim</span>
      </div>
      <div className="vl-kv">
        <span>Stok devir hızı (tahmini)</span>
        <span>{turnoverDays == null ? '—' : `≈ ${turnoverDays.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} gün`}</span>
      </div>

      {recentLoads.length > 0 && (
        <>
          <p className="vl-card-title" style={{ marginTop: 12 }}>Son Yüklemeler</p>
          <table className="vl-lines">
            <thead>
              <tr><th>Talep</th><th>Tarih</th><th>Durum</th><th className="num">Kalem</th><th className="num">Tutar</th></tr>
            </thead>
            <tbody>
              {recentLoads.map((r) => (
                <tr key={pick(r, 'id', 'Id')}>
                  <td className="vl-mono">{pick(r, 'requestNo', 'RequestNo') || '—'}</td>
                  <td>{formatDate(pick(r, 'deliveryDate', 'DeliveryDate'))}</td>
                  <td>{statusBadge(pick(r, 'status', 'Status'))}</td>
                  <td className="num">{pick(r, 'distinctSku', 'DistinctSku') ?? '-'}</td>
                  <td className="num">{formatMoney(pick(r, 'netAmount', 'NetAmount'))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="vl-card-title" style={{ marginTop: 12 }}>Aracın Mevcut Stoğu</p>
      {stockRows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#64748b' }}>Araç deposunda stok yok (veya araç deposu çözülemedi).</div>
      ) : (
        <>
          <table className="vl-lines">
            <thead><tr><th>Ürün</th><th className="num">Araçta</th><th className="num">Talep</th></tr></thead>
            <tbody>
              {visibleStock.map((sRow) => {
                const req = requestedLines.find((l) => l.productId === sRow.productId);
                return (
                  <tr key={sRow.productId}>
                    <td>
                      {requestedIds.has(sRow.productId) && <span title="Bu talepte var" style={{ color: '#d97706' }}>★ </span>}
                      {sRow.productName}
                    </td>
                    <td className="num">{formatNumber(sRow.physical)}</td>
                    <td className="num">{req ? formatNumber(req.requestedQty) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stockRows.length > 6 && (
            <button type="button" className="vl-chip" style={{ marginTop: 6 }} onClick={() => setShowAllStock((v) => !v)}>
              {showAllStock ? 'Daha az göster' : `+ ${stockRows.length - 6} ürün daha`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default Plasiyer360Panel;
